import Auth0 from 'auth0-js';
import env from '../utils/env';

import { setToken, setUser } from '../utils/cookie';
import { createUrl } from '../utils/url';

import type { LogoutOptions, WebAuth } from 'auth0-js';

const { AUTH0_DOMAIN, NEXT_PUBLIC_MICROSOFT_CONNECTION_NAME } = env;

type AuthCallback = (error: string | null) => void;

const determineLoginRedirectUrl = (host: string) => createUrl(host, '/login');

const startEmailPasswordless = async (
  email: string,
  locale: string,
  callback: AuthCallback,
) => {
  const response = await fetch(`https://${AUTH0_DOMAIN}/passwordless/start`, {
    method: 'POST',
    headers: {
      'x-request-language': locale,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      client_id: env.NEXT_PUBLIC_AUTH0_CLIENT_ID,
      connection: 'email',
      send: 'code',
      email,
    }),
  });
  if (response.ok) {
    return callback(null);
  }
  return response.text().then((text) => {
    try {
      const description = JSON.parse(text).error_description;
      callback(description);
    } catch (err) {
      callback(text);
    }
  });
};

// Returned to the verify callback when the request never produced an answer —
// timeout, offline, blocked by an extension. Distinct from a rejected code on
// purpose: `pin.tsx` used to report both as access_denied, which is why the
// login funnel read as "users cannot type their code" (DEV-656).
export const VERIFY_TRANSPORT_ERROR = 'VERIFY_TRANSPORT_ERROR';

// `pin.tsx` clears its spinner only from the callback, so a fetch that never
// settles leaves the user on a spinner forever with no error and no event.
const VERIFY_TIMEOUT_MS = 15000;

class AuthApi {
  webAuth: WebAuth | null;

  constructor() {
    this.webAuth = null;
  }

  getWebAuth(host: string): Auth0.WebAuth {
    if (!this.webAuth) {
      this.webAuth = new Auth0.WebAuth({
        clientID: env.NEXT_PUBLIC_AUTH0_CLIENT_ID,
        domain: AUTH0_DOMAIN,
        redirectUri: determineLoginRedirectUrl(host),
        responseType: 'code',
        scope: 'openid profile',
      });
    }
    return this.webAuth;
  }

  async startPasswordless(
    phoneNumber: string,
    email: string,
    altchaValue: string,
    locale: string,
  ) {
    const response = await fetch(`/api/passwordless-start`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        email: email?.trim(),
        phone: phoneNumber,
        altcha: altchaValue,
        locale,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error ?? 'Failed to start passwordless flow');
    }
    return data;
  }

  async verifyCode(
    phoneNumber: string,
    verificationCode: string,
    keepSignedIn: boolean,
    callback: AuthCallback,
  ) {
    let timer: ReturnType<typeof setTimeout>;

    try {
      // AbortController rather than AbortSignal.timeout: the latter throws on
      // Safari < 16, which is still in the field on school iPads, and the throw
      // would land in the catch below and make login impossible there.
      const controller = new AbortController();
      timer = setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);

      const response = await fetch('/api/passwordless-verify', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: phoneNumber,
          code: verificationCode,
          keepSignedIn,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.tokens) {
          window.location.href = '/login';
        } else {
          callback(null);
        }
      } else if (response.status >= 500) {
        // `/api/passwordless-verify` turns an Auth0 outage or an unexpected
        // server exception into a 500. `fetch` resolves fine, so without this
        // branch the caller reported infrastructure failures as access_denied,
        // spent one of the user's three attempts and told them their code was
        // wrong. Auth0's own rejection of a code comes back as a 400 and still
        // falls through below. See DEV-656.
        callback(VERIFY_TRANSPORT_ERROR);
      } else {
        callback(data.error ?? 'Verification failed');
      }
    } catch {
      // Includes the abort fired by VERIFY_TIMEOUT_MS. The specific message was
      // discarded by the caller anyway; what matters is that it was not a
      // rejected code.
      callback(VERIFY_TRANSPORT_ERROR);
    } finally {
      clearTimeout(timer);
    }
  }

  startEmailPasswordless = startEmailPasswordless;

  async verifyEmailCode(
    email: string,
    verificationCode: string,
    keepSignedIn: boolean,
    callback: AuthCallback,
  ) {
    let timer: ReturnType<typeof setTimeout>;

    try {
      // AbortController rather than AbortSignal.timeout: the latter throws on
      // Safari < 16, which is still in the field on school iPads, and the throw
      // would land in the catch below and make login impossible there.
      const controller = new AbortController();
      timer = setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);

      const response = await fetch('/api/passwordless-verify', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code: verificationCode,
          keepSignedIn,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.tokens) {
          window.location.href = '/login';
        } else {
          callback(null);
        }
      } else if (response.status >= 500) {
        // `/api/passwordless-verify` turns an Auth0 outage or an unexpected
        // server exception into a 500. `fetch` resolves fine, so without this
        // branch the caller reported infrastructure failures as access_denied,
        // spent one of the user's three attempts and told them their code was
        // wrong. Auth0's own rejection of a code comes back as a 400 and still
        // falls through below. See DEV-656.
        callback(VERIFY_TRANSPORT_ERROR);
      } else {
        callback(data.error ?? 'Verification failed');
      }
    } catch {
      // Includes the abort fired by VERIFY_TIMEOUT_MS. The specific message was
      // discarded by the caller anyway; what matters is that it was not a
      // rejected code.
      callback(VERIFY_TRANSPORT_ERROR);
    } finally {
      clearTimeout(timer);
    }
  }

  // Federated sign-in through a named Auth0 connection (Microsoft `waad`,
  // Skolon generic-OAuth2, later Google Workspace — DEV-677 / DEV-717).
  //
  // Client-side Auth0 redirect to the connection — NOT the server-side ROPC
  // the student roster flow uses. Auth0 → identity provider → back to
  // peikko's `redirectPath` with the token in the URL hash, where the page
  // calls parseToken() to set the cookie.
  //
  // The connection name is operator config (case-sensitive per Auth0
  // tenant) and the connection must be enabled on the Peikko Backend Auth0
  // client, with `redirectPath` in its Allowed Callback URLs.
  //
  // `maxAge` (seconds) asks for a session no older than that, which is what
  // makes Auth0 put `auth_time` in the id_token at all — jakari's Skolon
  // freshness gate requires it. It must stay in step with jakari's
  // SKOLON_CLAIM_MAX_AGE_SECONDS.
  //
  // `prompt: 'login'` forces Auth0 to ignore its own SSO cookie and re-run
  // the connection, which stamps a fresh `auth_time` on the id_token. That
  // is what jakari's Skolon freshness check reads, and it is exactly what
  // the silent checkSession() renewal CANNOT refresh (it mints a new `iat`
  // and keeps the old `auth_time`). The user normally sees no prompt: the
  // upstream IdP still has its own session.
  //
  // `scope` narrows what we ask the IdP for. DEV-736's pupil door asks for
  // `openid` alone — it needs no profile or email — so the default is only
  // a default, not a floor.
  loginWith(
    host: string,
    connection: string,
    redirectPath: string,
    options: { prompt?: 'login'; maxAge?: number; scope?: string } = {},
  ) {
    if (!connection) {
      // Fail loud rather than bouncing the user into a broken Auth0
      // screen — surfaces the missing-config to operators immediately.
      throw new Error('Auth0 connection name is not configured.');
    }
    this.getWebAuth(host).authorize({
      connection,
      ...(options.prompt && { prompt: options.prompt }),
      // auth0-js snake-cases this to `max_age`. Per OIDC, `auth_time` is
      // only guaranteed in the id_token when the client asks for max_age —
      // and jakari now REQUIRES auth_time on the Skolon claim, so without
      // this every activation 401s. It also makes Auth0 re-authenticate
      // when its own session is older than the window, which re-runs
      // Skolon's licence check at the source instead of only detecting a
      // stale claim afterwards.
      ...(options.maxAge != null && { maxAge: options.maxAge }),
      // Only the id_token is consumed (see parseToken → authResult.idToken),
      // so we don't request an access `token` — that would put an unused
      // credential in the URL fragment. Still the implicit flow, so the
      // Peikko Backend client must have the "Implicit" grant type enabled.
      responseType: 'id_token',
      redirectUri: createUrl(host, redirectPath),
      scope: options.scope ?? 'openid profile email',
    });
  }

  // UK school-account login over Microsoft (`waad`), used by two doors:
  // the staff login (MVP — Westminster; spec
  // docs/plans/.../2026-06-03-teacher-microsoft-login-design.md §4) and
  // DEV-736's pupil door, which lands on its own page and asks for a
  // narrower scope.
  //
  // Thin wrapper over loginWith, which is what master's own note on this
  // function anticipated once DEV-677 landed the shared version. Defaults
  // reproduce the staff call exactly, so that path is untouched.
  // See NEXT_PUBLIC_MICROSOFT_CONNECTION_NAME in .env.example for the
  // operator steps.
  loginWithMicrosoft(
    host: string,
    options: { redirectPath?: string; scope?: string } = {},
  ) {
    const connection = NEXT_PUBLIC_MICROSOFT_CONNECTION_NAME;
    if (!connection) {
      throw new Error(
        'NEXT_PUBLIC_MICROSOFT_CONNECTION_NAME is not configured — ' +
          'set it to the Auth0 waad connection name.',
      );
    }
    this.loginWith(host, connection, options.redirectPath ?? '/login-staff', {
      ...(options.scope && { scope: options.scope }),
    });
  }

  parseToken(hash: string, host: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.getWebAuth(host).parseHash({ hash }, (err, authResult) => {
        if (err) {
          console.log({ err });
          reject(err);
        } else {
          const token = authResult.idToken;
          setToken(token);
          resolve(token);
        }
        },
      );
    });
  }

  // Silent renewal (checkSession, prompt=none). Without `scope` it uses the
  // WebAuth default ('openid profile'), unchanged for everyone else.
  //
  // DEV-794: a school-SSO pupil signed in with 'openid' alone, and a renewal
  // without a scope would hand peikko a fresh id_token carrying the child's
  // name/given_name/family_name/nickname/picture anyway. Callers renewing a
  // pupil's token pass SCHOOL_SSO_PUPIL_SCOPE; see renewTokenFor in
  // apis/IdentifyApi.
  async renewToken(
    host: string,
    options: { scope?: string } = {},
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.getWebAuth(host).checkSession(
        options.scope ? { scope: options.scope } : {},
        async (err, authResult) => {
        if (err) {
          console.log({ err });
          reject(err);
        } else {
          const token = authResult.idToken;
          setToken(token);
          resolve(token);
        }
        },
      );
    });
  }

  async logout(host: string, options: LogoutOptions) {
    setToken('', 0);
    setUser('', 0);
    return this.getWebAuth(host).logout(options);
  }
}

const authApi = new AuthApi();
export default authApi;
